import { doc, getDoc, setDoc, onSnapshot } from 'firebase/firestore';
import { db } from '../lib/firebase';
import { DbState, BusinessSettings } from '../types';
import { getBusinessSettings, saveBusinessSettings } from './businessSettings';
import { saveDb, getDbState } from '../dbService';

export interface UserCloudVaultData {
  dbState: DbState;
  businessSettings: BusinessSettings;
  updatedAt: string;
}

const PENDING_SYNC_KEY = 'azuraos_pending_cloud_sync_uid';
const LAST_SYNC_KEY = 'azuraos_last_cloud_sync_timestamp';

export function getLastSyncTimestamp(): string | null {
  try {
    return localStorage.getItem(LAST_SYNC_KEY);
  } catch (e) {
    return null;
  }
}

export function setLastSyncTimestamp(isoStr?: string) {
  try {
    const timestamp = isoStr || new Date().toISOString();
    localStorage.setItem(LAST_SYNC_KEY, timestamp);
    window.dispatchEvent(new CustomEvent('azuraos-sync-timestamp-updated', { detail: timestamp }));
  } catch (e) {
    // Ignore
  }
}

/**
 * Saves current local state and business settings to user's private Firestore document
 */
export async function syncStateToCloud(userId: string, customDbState?: DbState, customSettings?: BusinessSettings): Promise<boolean> {
  if (!userId) return false;

  const dbState = customDbState || getDbState();
  const businessSettings = customSettings || getBusinessSettings();

  // If offline, queue for background sync immediately
  if (typeof navigator !== 'undefined' && !navigator.onLine) {
    markPendingSync(userId);
    registerBackgroundSyncTag();
    return false;
  }

  try {
    const nowIso = new Date().toISOString();
    const userDocRef = doc(db, 'users', userId);
    await setDoc(userDocRef, {
      dbState,
      businessSettings,
      updatedAt: nowIso
    }, { merge: true });

    // Success - clear pending marker & record timestamp
    clearPendingSync();
    setLastSyncTimestamp(nowIso);
    return true;
  } catch (err) {
    console.warn('Network or Firestore sync error, queueing for background retry:', err);
    markPendingSync(userId);
    registerBackgroundSyncTag();
    return false;
  }
}

/**
 * Mark that a cloud sync is pending in localStorage
 */
function markPendingSync(userId: string) {
  try {
    localStorage.setItem(PENDING_SYNC_KEY, userId);
  } catch (e) {
    // Ignore quota errors
  }
}

/**
 * Clear pending sync flag
 */
function clearPendingSync() {
  try {
    localStorage.removeItem(PENDING_SYNC_KEY);
  } catch (e) {
    // Ignore
  }
}

/**
 * Checks if there is a pending sync queued
 */
export function hasPendingSync(): boolean {
  try {
    return !!localStorage.getItem(PENDING_SYNC_KEY);
  } catch (e) {
    return false;
  }
}

/**
 * Request Service Worker to register background sync tag
 */
export function registerBackgroundSyncTag() {
  if ('serviceWorker' in navigator && navigator.serviceWorker.controller) {
    navigator.serviceWorker.controller.postMessage({ type: 'REGISTER_SYNC' });
  } else if ('serviceWorker' in navigator) {
    navigator.serviceWorker.ready.then((reg) => {
      if ('sync' in reg) {
        (reg as any).sync.register('azuraos-vault-sync').catch(() => {});
      }
    });
  }
}

/**
 * Flush any queued sync if online
 */
export async function flushPendingSync(userId: string, onSyncSuccess?: () => void): Promise<boolean> {
  if (!userId || !navigator.onLine) return false;
  
  const pendingUid = localStorage.getItem(PENDING_SYNC_KEY);
  if (pendingUid && (pendingUid === userId || pendingUid === 'true')) {
    const success = await syncStateToCloud(userId);
    if (success && onSyncSuccess) {
      onSyncSuccess();
    }
    return success;
  }
  return false;
}

/**
 * Initialize background sync event listeners for reconnection and SW triggers
 */
export function initBackgroundSyncListeners(getUserId: () => string | undefined, onSyncComplete?: () => void): () => void {
  const handleOnline = async () => {
    const uid = getUserId();
    if (uid) {
      await flushPendingSync(uid, onSyncComplete);
    }
  };

  const handleSWMessage = async (event: MessageEvent) => {
    if (event.data && event.data.type === 'AZURAOS_TRIGGER_BACKGROUND_SYNC') {
      const uid = getUserId();
      if (uid) {
        await flushPendingSync(uid, onSyncComplete);
      }
    }
  };

  window.addEventListener('online', handleOnline);

  if ('serviceWorker' in navigator) {
    navigator.serviceWorker.addEventListener('message', handleSWMessage);
  }

  // Attempt initial flush if online
  if (navigator.onLine) {
    handleOnline();
  }

  return () => {
    window.removeEventListener('online', handleOnline);
    if ('serviceWorker' in navigator) {
      navigator.serviceWorker.removeEventListener('message', handleSWMessage);
    }
  };
}

/**
 * Sets up a real-time snapshot listener for the user's private Firestore vault
 */
export function subscribeToCloudVault(
  userId: string, 
  onRemoteChange: (vault: UserCloudVaultData) => void
): () => void {
  if (!userId) return () => {};

  const userDocRef = doc(db, 'users', userId);
  const unsubscribe = onSnapshot(userDocRef, (snapshot) => {
    if (snapshot.exists()) {
      const data = snapshot.data() as UserCloudVaultData;
      if (data && (data.dbState || data.businessSettings)) {
        // Save to local storage for offline resilience
        if (data.dbState) {
          saveDb(data.dbState);
        }
        if (data.businessSettings) {
          try {
            localStorage.setItem('solostack_business_settings', JSON.stringify(data.businessSettings));
            window.dispatchEvent(new CustomEvent('businessSettingsUpdated', { detail: data.businessSettings }));
          } catch (e) {
            console.error('Failed to update local business settings from cloud snapshot:', e);
          }
        }
        if (data.updatedAt) {
          setLastSyncTimestamp(data.updatedAt);
        }
        onRemoteChange(data);
      }
    }
  }, (error) => {
    if (error?.code === 'unavailable' || error?.message?.includes('unavailable')) {
      console.warn('Firestore vault operating in offline mode. Changes will sync when network is active.');
    } else {
      console.error('Firestore vault snapshot error:', error);
    }
  });

  return unsubscribe;
}

/**
 * One-time load from Firestore cloud vault on login
 */
export async function fetchCloudVault(userId: string): Promise<UserCloudVaultData | null> {
  if (!userId) return null;
  try {
    const userDocRef = doc(db, 'users', userId);
    const snapshot = await getDoc(userDocRef);
    if (snapshot.exists()) {
      return snapshot.data() as UserCloudVaultData;
    }
  } catch (err) {
    console.error('Error fetching cloud vault:', err);
  }
  return null;
}
